'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import { useProducts, useSetIngredients } from '@/hooks/use-products';
import { useCartStore } from '@/stores/cart-store';
import { useProductPopupStore } from '@/stores/product-popup-store';
import { parseAllergens } from '@/lib/allergens';
import { generateSlug } from '@/lib/seo/slug';
import { ymAddToCart, tmrAddToCart, ecomAdd } from '@/lib/analytics';
import type { ProductWithCategories } from '@/lib/api/products';
import { useStorefrontArt } from '@/lib/api/storefront-media';
import { ProductDetail, type CartAddition } from './product-detail';
import {
  isOrderable,
  paidQuantities,
  productPresentation,
  toDetailProduct,
  validateAdditions,
} from './catalog-model';
import './storefront.css';
import './integration.css';

export function StorefrontProductPopup() {
  const open = useProductPopupStore((s) => s.isOpen);
  const snapshot = useProductPopupStore((s) => s.product);
  const stack = useProductPopupStore((s) => s.viewStack);
  const close = useProductPopupStore((s) => s.closePopup);
  const push = useProductPopupStore((s) => s.pushProduct);
  const back = useProductPopupStore((s) => s.popProduct);
  const { data: products = [] } = useProducts({ enabled: open });
  const art = useStorefrontArt(undefined, { enabled: open });
  const product = products.find((p) => p.id === snapshot?.id) || snapshot;
  const isSet = !!product?.categories.some((c) => [2, 9, 16].includes(c.id));
  const ingredients = useSetIngredients(product?.id || 0, { enabled: open && isSet });
  const items = useCartStore((s) => s.items);
  const hydrated = useCartStore((s) => s._hasHydrated);
  const quantities = useMemo(() => (hydrated ? paidQuantities(items) : {}), [items, hydrated]);
  const sauces = products
    .filter((p) => p.categories.some((c) => c.id === 7) && isOrderable(p))
    .sort((a, b) => a.price - b.price)
    .map((p) => toDetailProduct(p, art[p.id]));
  if (!product) return null;
  const presentation = productPresentation(product, art[product.id]);
  const allergens = parseAllergens(product.allergens)
    .map((a) => a.label)
    .join(', ');
  const detail = { ...toDetailProduct(product, art[product.id]), allergens };
  const add = (additions: CartAddition[]) => {
    const cart = useCartStore.getState();
    if (!validateAdditions(additions, products, paidQuantities(cart.items))) return false;
    for (const item of additions) {
      const p = products.find((p) => p.id === item.id)!;
      cart.addItem(p, item.quantity);
      ymAddToCart(p.id, p.name, p.price, item.quantity);
      tmrAddToCart();
      ecomAdd({ id: String(p.id), name: p.name, price: p.price, quantity: item.quantity });
    }
    return true;
  };
  const openIngredient = (ingredient: ProductWithCategories) => {
    const actual = products.find((p) => p.id === ingredient.id);
    push(
      actual || {
        ...ingredient,
        categories: ingredient.categories || [],
        status: ingredient.status || 'unavailable',
      }
    );
  };
  return (
    <ProductDetail
      product={open ? detail : null}
      category={product.categories[0]?.name.trim() || 'Наше меню'}
      scene={presentation.scene}
      sauces={sauces}
      cart={quantities}
      onClose={close}
      onAdd={add}
      available={isOrderable(product) && products.some((p) => p.id === product.id)}
      onBack={stack.length ? back : undefined}
      parentName={stack.at(-1)?.name}
      extraContent={
        <>
          {isSet && (
            <section className="pd-section pd-reveal" aria-label="Состав сета">
              <h3>Состав сета</h3>
              {ingredients.isLoading ? (
                <p>Загружаем состав…</p>
              ) : ingredients.isError ? (
                <p>
                  Не удалось загрузить состав.{' '}
                  <button onClick={() => void ingredients.refetch()}>Повторить</button>
                </p>
              ) : ingredients.data?.length ? (
                <div className="pd-set-items">
                  {ingredients.data.map((p, index) => (
                    <button key={`${p.id}-${index}`} onClick={() => openIngredient(p)}>
                      <img src={productPresentation(p, art[p.id]).image} alt="" />
                      <span>{p.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p>Подробности состава уточняются.</p>
              )}
            </section>
          )}
          <div className="pd-extra-information">
            <p>Готовим на общей кухне — возможны следы других аллергенов.</p>
            <div className="pd-info-links">
              <Link href="/allergens" onClick={close}>
                Об аллергенах
              </Link>
              <Link href={`/product/${generateSlug(product.name, product.id)}`} onClick={close}>
                Страница товара
              </Link>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/product/${generateSlug(product.name, product.id)}`;
                  void navigator.clipboard?.writeText(url);
                }}
              >
                Скопировать ссылку
              </button>
            </div>
          </div>
        </>
      }
    />
  );
}
